import { Injectable, OnDestroy } from '@angular/core'
import { Observable, Subject, fromEvent, takeUntil } from 'rxjs'
import { Socket, io } from 'socket.io-client'
import { AuthService } from '../../../services/auth.service'
import { ServerUrlService } from '../../../services/server-url.service'
import { ChatEntity, ChatMessageEntity } from '../interfaces/chat.interface'

@Injectable({ providedIn: 'root' })
export class MessageChatSocketService implements OnDestroy {
  private socket: Socket | null = null
  private readonly destroy$ = new Subject<void>()

  private readonly newMessage$ = new Subject<ChatMessageEntity>()
  private readonly messageEdited$ = new Subject<ChatMessageEntity>()
  private readonly messageDeleted$ = new Subject<{ chatId: number; messageId: number }>()
  private readonly userTyping$ = new Subject<{ chatId: number; userId: number; userName: string }>()
  private readonly userStopTyping$ = new Subject<{ chatId: number; userId: number }>()
  private readonly userOnline$ = new Subject<{ userId: number }>()
  private readonly userOffline$ = new Subject<{ userId: number }>()
  private readonly participantJoined$ = new Subject<{
    chatId: number
    userId: number
    userName: string
  }>()
  private readonly participantLeft$ = new Subject<{ chatId: number; userId: number }>()
  private readonly chatCreated$ = new Subject<ChatEntity>()

  constructor(
    private readonly serverUrl: ServerUrlService,
    private readonly authService: AuthService
  ) {}

  connect(userId: number, userName: string): void {
    if (this.socket?.connected) {
      return
    }

    let url = this.serverUrl.url
    if (/:[0-9]{4}/.test(url)) {
      url = url.slice(0, url.length - 4) + '2023'
    }

    this.socket = io(url, {
      transports: ['websocket'],
      path: '/nest-ws',
      auth: { token: this.authService.token },
    })

    this.socket.on('connect', () => {
      this.socket!.emit('registerUser', { userId, userName })
    })

    fromEvent<ChatMessageEntity>(this.socket, 'newMessage')
      .pipe(takeUntil(this.destroy$))
      .subscribe((msg) => this.newMessage$.next(msg))

    fromEvent<ChatMessageEntity>(this.socket, 'messageEdited')
      .pipe(takeUntil(this.destroy$))
      .subscribe((msg) => this.messageEdited$.next(msg))

    fromEvent<{ chatId: number; messageId: number }>(this.socket, 'messageDeleted')
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => this.messageDeleted$.next(data))

    fromEvent<{ chatId: number; userId: number; userName: string }>(this.socket, 'userTyping')
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => this.userTyping$.next(data))

    fromEvent<{ chatId: number; userId: number }>(this.socket, 'userStopTyping')
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => this.userStopTyping$.next(data))

    fromEvent<{ userId: number }>(this.socket, 'userOnline')
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => this.userOnline$.next(data))

    fromEvent<{ userId: number }>(this.socket, 'userOffline')
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => this.userOffline$.next(data))

    fromEvent<{ chatId: number; userId: number; userName: string }>(
      this.socket,
      'participantJoined'
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => this.participantJoined$.next(data))

    fromEvent<{ chatId: number; userId: number }>(this.socket, 'participantLeft')
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => this.participantLeft$.next(data))

    fromEvent<ChatEntity>(this.socket, 'chatCreated')
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => this.chatCreated$.next(data))
  }

  joinChat(chatId: number): void {
    this.socket?.emit('joinChat', { chatId })
  }

  joinAllChats(chatIds: number[]): void {
    for (const chatId of chatIds) {
      this.socket?.emit('joinChat', { chatId })
    }
  }

  leaveChat(chatId: number): void {
    this.socket?.emit('leaveChat', { chatId })
  }

  emitTyping(chatId: number, userId: number, userName: string): void {
    this.socket?.emit('typing', { chatId, userId, userName })
  }

  emitStopTyping(chatId: number, userId: number): void {
    this.socket?.emit('stopTyping', { chatId, userId })
  }

  onNewMessage(): Observable<ChatMessageEntity> {
    return this.newMessage$.asObservable()
  }

  onMessageEdited(): Observable<ChatMessageEntity> {
    return this.messageEdited$.asObservable()
  }

  onMessageDeleted(): Observable<{ chatId: number; messageId: number }> {
    return this.messageDeleted$.asObservable()
  }

  onUserTyping(): Observable<{ chatId: number; userId: number; userName: string }> {
    return this.userTyping$.asObservable()
  }

  onUserStopTyping(): Observable<{ chatId: number; userId: number }> {
    return this.userStopTyping$.asObservable()
  }

  onUserOnline(): Observable<{ userId: number }> {
    return this.userOnline$.asObservable()
  }

  onUserOffline(): Observable<{ userId: number }> {
    return this.userOffline$.asObservable()
  }

  onParticipantJoined(): Observable<{ chatId: number; userId: number; userName: string }> {
    return this.participantJoined$.asObservable()
  }

  onParticipantLeft(): Observable<{ chatId: number; userId: number }> {
    return this.participantLeft$.asObservable()
  }

  onChatCreated(): Observable<ChatEntity> {
    return this.chatCreated$.asObservable()
  }

  disconnect(): void {
    this.socket?.disconnect()
    this.socket = null
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
    this.disconnect()
  }
}
